import { usePostHogCapture } from '@app/core/integrations/posthog/usePostHogCapture';
import { Box, ChipProps } from '@mui/material';
import { useRef, useState } from 'react';
import { StationsNavigatorLink } from './StationsNavigatorLink';
import { StationsNavigatorPopover } from './selector/StationsSelector';
import { Station } from '@app/core/types/stations';

interface StationNavigatorProps {
  readonly activeId: Station['id'];
  readonly size?: ChipProps['size'];
  readonly onSelectStation: (station: Station) => void;
  readonly stations: readonly Station[];
  readonly recentStationIds?: readonly string[];
}

/**
 * UI component for station navigation. Displays the active station and a popover
 * for selecting other stations. The selection logic is handled by the parent component
 * via the onSelectStation prop.
 */
export const StationNavigator = (props: StationNavigatorProps) => {
  const { size = 'medium', stations, recentStationIds = [] } = props;
  const capture = usePostHogCapture();
  const anchorRef = useRef<HTMLSpanElement | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const activeStation = stations.find((s) => s.id === props.activeId);
  const isOpen = Boolean(anchorEl);

  const handleOpenSelector = () => {
    setAnchorEl(anchorRef.current);
  };

  const handleSelectStation = (station: Station) => {
    capture('stations:navigation_selected', {
      from_station: activeStation?.name,
      to_station: station.name,
    });

    setAnchorEl(null);
    // Use setTimeout to defer selection until after popover close animation starts
    // This ensures the popover begins closing before the selection triggers any UI updates
    setTimeout(() => {
      props.onSelectStation(station);
    }, 0);
  };

  return (
    <>
      <Box
        component="span"
        ref={anchorRef}
        sx={{ fontWeight: '400', display: 'inline-flex', alignItems: 'center' }}
      >
        <StationsNavigatorLink
          station={activeStation}
          size={props.size}
          onClick={handleOpenSelector}
        />
      </Box>

      <StationsNavigatorPopover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        stations={stations}
        activeId={props.activeId}
        onSelectStation={handleSelectStation}
        recentStationIds={recentStationIds}
        size={size}
      />
    </>
  );
};
